import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "@emotion/styled";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Snackbar, { SnackbarCloseReason } from "@mui/material/Snackbar";
import Tooltip from "@mui/material/Tooltip";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { MainContext } from "./Main";
import {
  getAsanaCustomFieldList,
  getCustomFieldList,
  getProfile,
  postAddCustomField,
  postRemoveCustomField,
} from "../utils/api";
import { ICustomField, IProfile } from "../interfaces";

const MISSING_FIELD_TOOLTIP =
  "This custom field no longer exists in your Asana workspace, or your authorized Asana user no longer has access to it. Remove it from this list or restore it in Asana.";

const CustomFieldEditor = () => {
  const context = useContext(MainContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [searching, setSearching] = useState<boolean>(false);
  const [addingGid, setAddingGid] = useState<string | null>(null);
  const [removingGid, setRemovingGid] = useState<string | null>(null);
  const [snackOpen, setSnackOpen] = useState<boolean>(false);
  const [snackbarContent, setSnackbarContent] = useState<string>("");
  const [selectedCustomFields, setSelectedCustomFields] = useState<
    ICustomField[]
  >([]);
  const [searchResults, setSearchResults] = useState<ICustomField[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [profileId, setProfileId] = useState<string | null>(
    useParams().profileId || null,
  );
  const [profile, setProfile] = useState<IProfile | null>(null);

  useEffect(() => {
    const getCustomFieldListAsync = async (localProfileId: string) => {
      const customFieldListResponse = await getCustomFieldList(localProfileId);
      setSelectedCustomFields(customFieldListResponse);

      setLoading(false);
    };

    const getProfileAsync = async (profileId: string) => {
      const profileResponse = await getProfile(profileId);
      setProfile(profileResponse.profile);
    };

    if (profileId === null) {
      if (context.user) {
        setProfileId(context.user.profileId);
        getCustomFieldListAsync(context.user.profileId);
      } else {
        navigate("/login");
      }
    } else {
      getProfileAsync(profileId);
      getCustomFieldListAsync(profileId);
    }
  }, [context.user, navigate, profileId]);

  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackOpen(false);
  };

  const showSnackbar = (message: string) => {
    setSnackbarContent(message);
    setSnackOpen(true);
  };

  const isFieldSelected = (gid: string) =>
    selectedCustomFields.some((field) => field.gid === gid);

  const handleRemoveField = async (fieldId: string) => {
    setRemovingGid(fieldId);
    try {
      await postRemoveCustomField(profileId as string, fieldId);
      setSelectedCustomFields((prevFields) =>
        prevFields.filter((field) => field.gid !== fieldId),
      );
    } catch (e) {
      console.error(e);
      showSnackbar("Error removing custom field");
    }
    setRemovingGid(null);
  };

  const handleAddField = async (field: ICustomField) => {
    if (!field.gid || !field.name || isFieldSelected(field.gid)) {
      return;
    }

    setAddingGid(field.gid);
    try {
      const addedField = await postAddCustomField(profileId as string, field);
      setSelectedCustomFields((prevFields) => [...prevFields, addedField]);
      showSnackbar("Custom field added");
    } catch (e) {
      console.error(e);
      showSnackbar("Error adding custom field");
    }
    setAddingGid(null);
  };

  const handleSearch = async () => {
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) {
      setSearchResults([]);
      showSnackbar("Enter a custom field name to search");
      return;
    }

    setSearching(true);
    try {
      const results = await getAsanaCustomFieldList(
        trimmedQuery,
        profileId as string,
      );
      setSearchResults(results);
    } catch (e) {
      console.error(e);
      setSearchResults([]);
      showSnackbar("Error searching for custom fields");
    }
    setSearching(false);
  };

  return (
    <Styled>
      {!(
        profile?.asanaMembershipActive ||
        profile?.asanaFreeTrialActive ||
        context?.user?.asanaMembershipActive ||
        context?.user?.asanaFreeTrialActive
      ) ? (
        <>
          <Typography variant="h3" paragraph>
            This feature requires a paid plan or active free trial
          </Typography>

          <Typography variant="body1" paragraph>
            <Button variant="contained" href={`/asana-pricing/${profileId}/`}>
              Upgrade now
            </Button>
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="h1">Custom Field Editor</Typography>
          <Typography variant="body1" paragraph>
            Here you can choose which Asana custom fields you can edit in
            Intercom when creating a task.
          </Typography>
          <Typography variant="body1" paragraph>
            Search for custom fields in Asana by name and add them to your
            Selected Custom Fields list. If you don't see the custom field
            you're looking for, you may need to add the project field to your
            Asana workspace's field library. More info in{" "}
            <a
              href="https://thackd.com/asana-in-the-inbox#select-custom-fields"
              target="_blank"
              rel="noopener noreferrer"
            >
              our docs
            </a>
            .
          </Typography>

          {loading ? (
            <div className="loading-container">
              <CircularProgress />
            </div>
          ) : (
            <div className="content">
              <div className="search-container">
                <Typography variant="h3">Add Custom Fields</Typography>
                <TextField
                  label="Custom Field search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <LoadingButton
                  loading={searching}
                  variant="contained"
                  onClick={handleSearch}
                  disabled={!searchQuery.trim()}
                  className="search-button"
                >
                  Search
                </LoadingButton>
                <div className="search-results">
                  <Typography variant="h4" className="search-results-title">
                    Search Results
                  </Typography>
                  {searchResults.length === 0 ? (
                    <Typography variant="body1">No results found</Typography>
                  ) : (
                    <div className="field-list">
                      {searchResults.map((field) => (
                        <React.Fragment key={field.gid}>
                          <Typography variant="body1" className="field-name">
                            {field.name}
                          </Typography>
                          <LoadingButton
                            loading={addingGid === field.gid}
                            variant="contained"
                            onClick={() => handleAddField(field)}
                            disabled={isFieldSelected(field.gid)}
                          >
                            {isFieldSelected(field.gid) ? "Added" : "Add"}
                          </LoadingButton>
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="selected-fields-container">
                <Typography variant="h3">Selected Custom Fields</Typography>
                {selectedCustomFields.length === 0 ? (
                  <Typography variant="body1">
                    No custom fields selected.
                  </Typography>
                ) : (
                  <div className="field-list">
                    {selectedCustomFields.map((field) => (
                      <React.Fragment key={field.gid}>
                        <div className="field-name-row">
                          <Typography variant="body1" className="field-name">
                            {field.name}
                          </Typography>
                          {field.existsInWorkspace === false && (
                            <Tooltip title={MISSING_FIELD_TOOLTIP}>
                              <WarningAmberIcon
                                className="warning-icon"
                                fontSize="small"
                                color="warning"
                              />
                            </Tooltip>
                          )}
                        </div>
                        <LoadingButton
                          loading={removingGid === field.gid}
                          variant="contained"
                          onClick={() => handleRemoveField(field.gid)}
                        >
                          Remove
                        </LoadingButton>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <Snackbar
            open={snackOpen}
            autoHideDuration={3000}
            onClose={handleClose}
            message={snackbarContent}
          />
        </>
      )}
    </Styled>
  );
};

const Styled = styled.div`
  .MuiTypography-h1 {
    margin-bottom: 1rem;
  }

  .italic {
    font-style: italic;
  }

  .content {
    display: flex;
    align-items: flex-start;
    width: 100%;
    margin-top: 2rem;

    .MuiTypography-h3 {
      margin-bottom: 1rem;
    }

    .MuiTypography-body1 {
      margin-bottom: 0.3rem;
    }

    .selected-fields-container {
      margin-left: 10rem;
    }

    .field-list {
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 0.5rem 1rem;
      align-items: center;
      max-width: 480px;
    }

    .field-name-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      min-width: 0;
    }

    .field-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-bottom: 0;
    }

    .warning-icon {
      flex-shrink: 0;
      cursor: help;
    }

    .search-container {
      display: flex;
      flex-direction: column;
      margin-left: 2rem;

      .search-button {
        margin-top: 1rem;
        margin-bottom: 3rem;
      }

      .search-results {
        margin-top: 1rem;

        .search-results-title {
          margin-bottom: 1rem;
        }
      }
    }
  }

  .loading-container {
    display: flex;
    justify-content: center;
    width: 500px;
    max-width: 100%;
    margin-top: 2rem;
  }
`;

export default CustomFieldEditor;
